import { type PointerEvent as ReactPointerEvent, useCallback, useEffect } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { selectionResizeHandlesShouldStack } from '@/components/app/editor/selection-chrome/resize-handle-positions'
import { isShapeGeometryGestureBlocked } from '@/lib/groups/element-edit-policy'
import { createUnifiedSelectionResizeSession } from '@/lib/transforms/unified-selection-resize'
import { isCornerResizeHandle } from '@/lib/transforms/unified-selection-oriented-resize'
import { getResizeCursorForHandle } from '@/lib/transforms/selection-resize-cursor'
import {
  resolveGroupTransformInitials,
  resolveMultiMoveInitials,
  resolveMultiRotateStart,
} from '../gestures/interaction-start'
import { isShapeSelectable } from '../helpers/selection-scope'
import { setPointerCaptureIfSupported } from '../helpers/pointer-capture'
import type { InteractionState, ResizeHandle } from '../types/interaction-state'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { EditScopeRefsApi } from './use-edit-scope-refs'
import type { SelectionPresentationApi } from './use-selection-presentation'
import type { FeedbackPulsesApi } from './use-feedback-pulses'
import type { AltDragDuplicateApi } from './use-alt-drag-duplicate'

export function useGestureStart(
  ctx: ShapeManagerStateApi &
    EditScopeRefsApi &
    SelectionPresentationApi &
    FeedbackPulsesApi &
    AltDragDuplicateApi
) {
  const {
    duplicateSelectionForAltDrag,
    editScopeGroupIdRef,
    handlePointerMoveBridge,
    handlePointerUpBridge,
    interactionRef,
    multiSelectChromeRef,
    onCornerResizeGestureActiveChangeRef,
    onUserEdit,
    pendingRotateChromeBeforeRef,
    promoteAltDragDuplicateRef,
    savedBodyCursorForMultiMoveRef,
    scale,
    selectedShapeIdsRef,
    selectedShapes,
    setIsResizeGestureActive,
    shapeBounds,
    shapesById,
    shapesRef,
    triggerFrozenGeometryBlockedPulse,
  } = ctx

  // A move that starts on a shape body drives the shape under the pointer; anything else drives the
  // first selected shape.
  const resolveGesturePrimary = useCallback(
    (
      selected: ShapeData[],
      clickedShapeInMultiSelect: string | undefined,
      fromShapeBody: boolean
    ): ShapeData | undefined => {
      const primaryFromBody =
        fromShapeBody && clickedShapeInMultiSelect
          ? shapesRef.current.find(
              (s) =>
                s.id === clickedShapeInMultiSelect &&
                isShapeSelectable(s, editScopeGroupIdRef.current)
            )
          : undefined

      return primaryFromBody ?? selected[0]
    },
    [editScopeGroupIdRef, shapesRef]
  )

  const startInteraction = useCallback(
    (
      event: ReactPointerEvent,
      type: InteractionState['type'],
      direction?: ResizeHandle,
      clickedShapeInMultiSelect?: string,
      fromShapeBody = false,
      /**
       * Rotation centre in client coordinates. The visual layer has no element here to measure a
       * bounding rect from, so it passes the centre directly. Only the centre is ever read from
       * `overlayRect` (see the `rotate` branches in the pointer handlers), so a zero-size rect at
       * that point is equivalent — required in practice, since chrome only starts a rotate gesture
       * through `beginChromeRotate`, which always supplies it.
       */
      rotationCenterClient?: { x: number; y: number }
    ) => {
      const primary = resolveGesturePrimary(
        selectedShapes,
        clickedShapeInMultiSelect,
        fromShapeBody
      )

      if (!primary) {
        return
      }

      if (isShapeGeometryGestureBlocked(primary, shapesById)) {
        // Do not start a move/resize/rotate session; still consume the event so the pasteboard
        // does not treat this as an empty-canvas click (marquee would clear selection).
        event.preventDefault()
        event.stopPropagation()
        triggerFrozenGeometryBlockedPulse(primary)
        return
      }

      const chromeAtStart = multiSelectChromeRef.current

      let unifiedSelectionResizeSession: InteractionState['unifiedSelectionResizeSession']

      if (type === 'resize' && direction && selectedShapes.length > 1) {
        unifiedSelectionResizeSession =
          createUnifiedSelectionResizeSession({
            direction,
            selectedShapes: selectedShapes.map((shape) => ({
              id: shape.id,
              x: shape.x,
              y: shape.y,
              width: shape.width ?? 0,
              height: shape.height ?? 0,
              rotation: shape.rotation ?? 0,
              photoFill: shape.photoFill,
            })),
            shapeBoundsById: Object.fromEntries(
              selectedShapes.map((shape) => [shape.id, shapeBounds[shape.id]])
            ),
            chromeAtStart,
          }) ?? undefined

        if (!unifiedSelectionResizeSession) return
      }

      onUserEdit?.(
        (selectedShapes.length > 0 ? selectedShapes : [primary]).map((shape) => shape.id)
      )

      event.preventDefault()
      event.stopPropagation()
      // preventDefault above suppresses the browser's default focus-blur, so a toolbar
      // button focused by a prior action (e.g. focusSelectTool() after delete) stays
      // focused — which then swallows the next Delete/Backspace keypress via
      // isEditableTarget. Blur explicitly to release it.
      ;(document.activeElement as HTMLElement | null)?.blur()

      setPointerCaptureIfSupported(event.currentTarget, event.pointerId)

      const overlayRect = rotationCenterClient
        ? new DOMRect(rotationCenterClient.x, rotationCenterClient.y, 0, 0)
        : undefined
      const rotationStartAngle =
        type === 'rotate' && overlayRect
          ? Math.atan2(
              event.clientY - (overlayRect.top + overlayRect.height / 2),
              event.clientX - (overlayRect.left + overlayRect.width / 2)
            )
          : undefined

      const multiMoveInitials = resolveMultiMoveInitials({
        type,
        fromShapeBody,
        effectiveClickedShapeInMultiSelect: clickedShapeInMultiSelect,
        effectiveSelectedShapes: selectedShapes,
        selectedIds: selectedShapeIdsRef.current,
        shapes: shapesRef.current,
      })

      const multiChromePivotStart: InteractionState['multiChromePivotStart'] =
        (type === 'move' && multiMoveInitials && chromeAtStart) ||
        (type === 'rotate' && selectedShapes.length > 1 && chromeAtStart)
          ? { x: chromeAtStart.pivotX, y: chromeAtStart.pivotY }
          : undefined

      const {
        multiRotateInitials,
        rotatePivotLocal,
        multiChromeRotationStartDeg,
        multiChromeSizeStart,
      } = resolveMultiRotateStart({
        type,
        selectedShapes,
        chromeAtStart,
        pendingRotateChromeBeforeRef,
      })

      const { groupTransformInitials, groupPhotoFillInitials } = resolveGroupTransformInitials({
        effectiveSelectedShapes: selectedShapes,
        primary,
        shapes: shapesRef.current,
      })

      // Store interaction state
      interactionRef.current = {
        type,
        pointerId: event.pointerId,
        shapeId: primary.id,
        startClientX: event.clientX,
        startClientY: event.clientY,
        initial: { ...primary },
        direction,
        overlayRect,
        rotationStartAngle,
        aspectLockAxis: undefined,
        multiMoveInitials,
        multiRotateInitials,
        rotatePivotLocal,
        multiChromePivotStart,
        multiChromeRotationStartDeg,
        multiChromeSizeStart,
        clickedShapeInMultiSelect,
        unifiedSelectionResizeSession,
        groupTransformInitials,
        groupPhotoFillInitials,
        resizeHandlesStackedAtStart:
          type === 'resize'
            ? selectionResizeHandlesShouldStack(primary.width ?? 0, primary.height ?? 0, scale)
            : undefined,
        fromShapeBody,
        altDragDuplicatePending: type === 'move' && event.altKey,
      }

      if (type === 'resize') {
        setIsResizeGestureActive(true)

        if (direction && isCornerResizeHandle(direction)) {
          onCornerResizeGestureActiveChangeRef.current?.(true)
        }
      }

      if (multiMoveInitials) {
        savedBodyCursorForMultiMoveRef.current = document.body.style.cursor
        document.body.style.cursor = 'move'
      } else if (type === 'resize' && direction) {
        savedBodyCursorForMultiMoveRef.current = document.body.style.cursor
        const resizeRotation =
          unifiedSelectionResizeSession?.chromeStart.rotationDeg ?? primary.rotation ?? 0

        document.body.style.cursor = getResizeCursorForHandle(direction, resizeRotation)
      }

      // Attach stable bridge listeners so pointerup cleanup is not dropped by re-render.
      window.addEventListener('pointermove', handlePointerMoveBridge)
      window.addEventListener('pointerup', handlePointerUpBridge, true)
      window.addEventListener('pointercancel', handlePointerUpBridge, true)
    },
    [
      handlePointerMoveBridge,
      handlePointerUpBridge,
      onUserEdit,
      scale,
      selectedShapes,
      shapeBounds,
      shapesById,
      triggerFrozenGeometryBlockedPulse,
      resolveGesturePrimary,
      interactionRef,
      multiSelectChromeRef,
      onCornerResizeGestureActiveChangeRef,
      pendingRotateChromeBeforeRef,
      savedBodyCursorForMultiMoveRef,
      selectedShapeIdsRef,
      setIsResizeGestureActive,
      shapesRef,
    ]
  )

  /**
   * Alt+drag duplicates the selection in place and drags the copy, leaving the original where it
   * was. The pointer move and up handlers call this once the pointer has passed the click threshold,
   * so an Alt+Click (which cycles selection) never makes a copy (WMPCM-12824). The copies sit exactly
   * on the originals, so the gesture keeps its start point and pivots and only swaps which shapes it
   * drives. When nothing can be duplicated (a locked shape in the selection) it stays a normal move.
   */
  const promoteAltDragDuplicate = useCallback(() => {
    const interaction = interactionRef.current

    if (!interaction?.altDragDuplicatePending) return

    const duplicated = duplicateSelectionForAltDrag()
    const fromShapeBody = interaction.fromShapeBody ?? false
    const clickedShapeInMultiSelect =
      duplicated && interaction.clickedShapeInMultiSelect
        ? (duplicated.sourceIdToNewId.get(interaction.clickedShapeInMultiSelect) ??
          interaction.clickedShapeInMultiSelect)
        : interaction.clickedShapeInMultiSelect
    const primary = duplicated
      ? resolveGesturePrimary(duplicated.shapes, clickedShapeInMultiSelect, fromShapeBody)
      : undefined

    if (!duplicated || !primary) {
      interactionRef.current = { ...interaction, altDragDuplicatePending: false }
      return
    }

    onUserEdit?.(duplicated.shapes.map((shape) => shape.id))

    interactionRef.current = {
      ...interaction,
      shapeId: primary.id,
      initial: { ...primary },
      clickedShapeInMultiSelect,
      multiMoveInitials: resolveMultiMoveInitials({
        type: 'move',
        fromShapeBody,
        effectiveClickedShapeInMultiSelect: clickedShapeInMultiSelect,
        effectiveSelectedShapes: duplicated.shapes,
        selectedIds: selectedShapeIdsRef.current,
        shapes: shapesRef.current,
      }),
      ...resolveGroupTransformInitials({
        effectiveSelectedShapes: duplicated.shapes,
        primary,
        shapes: shapesRef.current,
      }),
      altDragDuplicatePending: false,
    }
  }, [
    duplicateSelectionForAltDrag,
    onUserEdit,
    resolveGesturePrimary,
    interactionRef,
    selectedShapeIdsRef,
    shapesRef,
  ])

  useEffect(() => {
    promoteAltDragDuplicateRef.current = promoteAltDragDuplicate
  }, [promoteAltDragDuplicate, promoteAltDragDuplicateRef])

  const handleMoveStart = useCallback(
    (event: ReactPointerEvent, clickedShapeInMultiSelect?: string, fromShapeBody = false) => {
      if (event.button !== 0) return
      startInteraction(event, 'move', undefined, clickedShapeInMultiSelect, fromShapeBody)
    },
    [startInteraction]
  )

  const handleResizeStart = useCallback(
    (event: ReactPointerEvent, direction: ResizeHandle) => {
      if (event.button !== 0) return
      event.stopPropagation()
      startInteraction(event, 'resize', direction)
    },
    [startInteraction]
  )

  const handleRotateStart = useCallback(
    (event: ReactPointerEvent, rotationCenterClient?: { x: number; y: number }) => {
      if (event.button !== 0) return
      event.stopPropagation()
      startInteraction(event, 'rotate', undefined, undefined, false, rotationCenterClient)
    },
    [startInteraction]
  )

  return { startInteraction, handleMoveStart, handleResizeStart, handleRotateStart }
}

export type GestureStartApi = ReturnType<typeof useGestureStart>
